import React, { useState } from 'react';
import { useAppData } from '../hooks/useAppData';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Button } from './ui/button';
import { Combobox } from './ui/combobox';
import { X, ChevronDown, ChevronRight, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';

export function EstadoDeCuenta() {
  const { lots, expenses } = useAppData();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<any>(null);
  const [mostrar, setMostrar] = useState(false);
  const [expandedPayments, setExpandedPayments] = useState<Set<number>>(new Set());

  // Filtrar lotes individuales que ya están en un grupo
  const lotsFiltrados = lots.filter((lot: any) => {
    if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) return true; // grupo
    // individual: mostrar solo si no está en ningún grupo
    const perteneceAGrupo = lots.some((l2: any) => Array.isArray(l2.idLotes) && l2.idLotes.includes(lot.id));
    return !perteneceAGrupo;
  });

  // Agrupar lotes por comprador y grupo (mismo buyerDPI, seller, saleDate, recibo, idLotes)
  const gruposMap = new Map<string, any>();
  lotsFiltrados.forEach((lot: any) => {
    // Clave única por grupo: comprador, vendedor, fecha, recibo, idLotes (o id si es individual)
    const key = [lot.buyerDPI, lot.seller, lot.saleDate, lot.receiptNumber, Array.isArray(lot.idLotes) ? lot.idLotes.join('-') : lot.id].join('|');
    if (!gruposMap.has(key)) {
      gruposMap.set(key, { ...lot, lotes: Array.isArray(lot.idLotes) ? lot.idLotes : [lot.id] });
    }
  });
  const grupos = Array.from(gruposMap.values());

  // Opciones de búsqueda unificadas
  const opciones = grupos.map((g: any) => ({
    label: `${g.buyerName} – ${g.buyerDPI} (Lotes: ${g.lotes.join(', ')})`,
    value: [g.buyerDPI, g.seller, g.saleDate, g.receiptNumber, g.lotes.join('-')].join('|'),
    type: 'grupo',
    grupo: g
  }));

  // Filtrar opciones por búsqueda (nombre, DPI o cualquier lote)
  const opcionesFiltradas = opciones.filter(opt => {
    const searchLower = search.toLowerCase();
    return (
      opt.label.toLowerCase().includes(searchLower) ||
      opt.grupo.buyerName?.toLowerCase().includes(searchLower) ||
      opt.grupo.buyerDPI?.toLowerCase().includes(searchLower) ||
      opt.grupo.lotes.some((l: string) => l.toString().includes(searchLower))
    );
  });

  // Al seleccionar, guardar el grupo seleccionado
  const handleSelect = (value: string) => {
    const found = opciones.find(opt => opt.value === value);
    setSelected(found);
    setMostrar(false);
  };

  // Limpiar selección
  const handleLimpiar = () => {
    setSelected(null);
    setMostrar(false);
    setSearch('');
    setExpandedPayments(new Set());
  };

  // Toggle para expandir/colapsar detalles de pago
  const togglePaymentDetails = (idx: number) => {
    const newExpanded = new Set(expandedPayments);
    if (newExpanded.has(idx)) {
      newExpanded.delete(idx);
    } else {
      newExpanded.add(idx);
    }
    setExpandedPayments(newExpanded);
  };

  // Función para exportar estados de cuenta de todos los clientes
  const exportarEstadosCuentaMasivo = () => {
    const workbook = XLSX.utils.book_new();
    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;

    // Procesar cada grupo de lotes (cliente)
    grupos.forEach((grupo: any) => {
      const lotesClienteExport = [grupo];
      let historialExport: any[] = [];
      let cuotasPendientesExport: any[] = [];

      lotesClienteExport.forEach((lot: any) => {
        if (lot.paymentType === 'pagos') {
          const numPagos = lot.numberOfPayments || lot.numeroPagos || 0;
          const totalValue = lot.totalValue || 0;
          const enganche = (lot.payments || []).find((p: any) => p.type === 'enganche')?.amount || 0;
          const saldo = totalValue - enganche;
          const cuotaMensual = numPagos > 0 ? saldo / numPagos : 0;

          // Historial de pagos
          const pagosPorRecibo = new Map<string, any[]>();
          (lot.payments || []).forEach((p: any) => {
            const recibo = p.receipt || p.receiptNumber || '-';
            if (!pagosPorRecibo.has(recibo)) {
              pagosPorRecibo.set(recibo, []);
            }
            pagosPorRecibo.get(recibo)!.push(p);
          });

          pagosPorRecibo.forEach((pagosGrupo, recibo) => {
            const enganchePago = pagosGrupo.find((p: any) => p.type === 'enganche');
            if (enganchePago) {
              historialExport.push({
                Fecha: enganchePago.date,
                Concepto: 'Enganche',
                TipoPago: 'Crédito',
                Recibo: recibo,
                Monto: enganchePago.amount,
                Estado: 'Pagado',
              });
              return;
            }

            const abonos = pagosGrupo.filter((p: any) => p.type === 'abono');
            if (abonos.length > 0) {
              if (abonos.length > 1) {
                const montoTotal = abonos.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
                let detalleCuotas = abonos.map((p: any) => `Cuota ${p.cuotaNumero}: Q.${Number(p.amount || 0).toFixed(2)}`).join('; ');
                historialExport.push({
                  Fecha: abonos[0].date,
                  Concepto: `Abono Manual (${detalleCuotas})`,
                  TipoPago: 'Crédito',
                  Recibo: recibo,
                  Monto: montoTotal,
                  Estado: 'Pagado',
                });
              } else {
                const p = abonos[0];
                const cuota = p.cuotaNumero ? String(p.cuotaNumero) : '-';
                historialExport.push({
                  Fecha: p.date,
                  Concepto: `Abono cuota ${cuota}`,
                  TipoPago: 'Crédito',
                  Recibo: recibo,
                  Monto: p.amount,
                  Estado: 'Pagado',
                });
              }
            }
          });

          // Cuotas pendientes
          const fechaVenta = new Date(lot.saleDate || new Date());
          for (let i = 1; i <= numPagos; i++) {
            const fechaVencimiento = new Date(fechaVenta);
            fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
            const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

            const pagosCuota = (lot.payments || []).filter((p: any) => {
              if (p.type !== 'abono') return false;
              const sameCuota = String(p.cuotaNumero) === String(i);
              const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
              const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaVencimientoYMD;
              return sameCuota && sameVenc;
            });
            const montoPagadoCuota = pagosCuota.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
            const montoRestante = Math.max(cuotaMensual - montoPagadoCuota, 0);
            const pagada = montoRestante <= 0.0001;

            cuotasPendientesExport.push({
              'Cuota No.': i,
              'Fecha Vencimiento': fechaVencimientoYMD,
              'Monto cuota': cuotaMensual.toFixed(2),
              'Pagado': montoPagadoCuota.toFixed(2),
              'Saldo': (pagada ? 0 : montoRestante).toFixed(2),
              'Estado': pagada ? 'Pagada' : 'Próxima',
            });
          }
        } else if (lot.paymentType === 'contado') {
          const idsGrupo = lot.idLotes ? (Array.isArray(lot.idLotes) ? lot.idLotes : String(lot.idLotes).split('-')) : [lot.id];
          const movsContado = expenses.filter((e: any) => {
            if (e.tipo !== 'contado') return false;
            const lotesMov = Array.isArray(e.lotes) ? e.lotes : [e.lotes];
            return idsGrupo.some((id: string) => lotesMov.includes(id));
          });
          movsContado.forEach((mov: any) => {
            historialExport.push({
              Fecha: mov.fecha,
              Concepto: mov.concepto,
              TipoPago: 'Contado',
              Recibo: mov.referencia || '-',
              Monto: mov.monto,
              Estado: mov.estado || 'Pagado',
            });
          });
        }
      });

      historialExport.sort((a, b) => new Date(b.Fecha).getTime() - new Date(a.Fecha).getTime());

      // Crear hoja con nombre del cliente (limitado a 31 caracteres para Excel)
      const nombreCliente = grupo.buyerName || 'Cliente';
      const nombreHoja = `${nombreCliente.substring(0, 25)}_${grupo.lotes[0]}`;

      // Información del cliente
      const infoCliente = [
        ['ESTADO DE CUENTA'],
        [''],
        ['Cliente:', grupo.buyerName || '-'],
        ['DPI:', grupo.buyerDPI || '-'],
        ['Lotes:', grupo.lotes.join(', ')],
        ['Vendedor:', grupo.seller || '-'],
        ['Valor Total:', `Q. ${(grupo.totalValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`],
        [''],
        ['HISTORIAL DE PAGOS'],
      ];

      const historialData = historialExport.map(p => [
        p.Fecha,
        p.Concepto,
        p.TipoPago,
        p.Recibo,
        `Q. ${parseFloat(p.Monto).toFixed(2)}`,
        p.Estado
      ]);

      const cuotasData = cuotasPendientesExport.length > 0 ? [
        [''],
        ['CUOTAS PENDIENTES'],
        ['Cuota No.', 'Fecha Vencimiento', 'Monto cuota', 'Pagado', 'Saldo', 'Estado'],
        ...cuotasPendientesExport.map(c => [
          c['Cuota No.'],
          c['Fecha Vencimiento'],
          `Q. ${c['Monto cuota']}`,
          `Q. ${c['Pagado']}`,
          `Q. ${c['Saldo']}`,
          c['Estado']
        ])
      ] : [];

      const allData = [
        ...infoCliente,
        ['Fecha', 'Concepto', 'Tipo de Pago', 'No. Recibo', 'Monto', 'Estado'],
        ...historialData,
        ...cuotasData
      ];

      const ws = XLSX.utils.aoa_to_sheet(allData);
      XLSX.utils.book_append_sheet(workbook, ws, nombreHoja);
    });

    // Guardar archivo
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `estados_cuenta_${dateStr}.xlsx`);
  };

  // Obtener lotes del grupo seleccionado
  let lotesCliente: any[] = [];
  if (selected && selected.type === 'grupo') {
    lotesCliente = [selected.grupo];
  }

  // Historial de pagos y cuotas pendientes
  let historialPagos: any[] = [];
  let cuotasPendientes: any[] = [];
  let esCredito = false;
  let esContado = false;

  if (mostrar && lotesCliente.length > 0) {
    lotesCliente.forEach((lot: any) => {
      if (lot.paymentType === 'contado') {
        esContado = true;
        // Movimientos de contado asociados
        const idsGrupo = lot.idLotes ? (Array.isArray(lot.idLotes) ? lot.idLotes : String(lot.idLotes).split('-')) : [lot.id];
        const movsContado = expenses.filter((e: any) => {
          if (e.tipo !== 'contado') return false;
          const lotesMov = Array.isArray(e.lotes) ? e.lotes : [e.lotes];
          return idsGrupo.some((id: string) => lotesMov.includes(id));
        });
        movsContado.forEach((mov: any) => {
          historialPagos.push({
            fecha: mov.fecha,
            concepto: mov.concepto,
            tipoPago: 'Contado',
            recibo: mov.referencia || '-',
            monto: mov.monto,
            estado: mov.estado || 'Pagado',
            esGrupo: false,
          });
        });
      } else if (lot.paymentType === 'pagos') {
        esCredito = true;
        // Calcular cuota mensual y construir historial detallado de pagos (incluyendo cuota/estado)
        const numPagos = lot.numberOfPayments || lot.numeroPagos || 0;
        const totalValue = lot.totalValue || 0;
        const enganche = (lot.payments || []).find((p: any) => p.type === 'enganche')?.amount || 0;
        const saldo = totalValue - enganche;
        const cuotaMensual = numPagos > 0 ? saldo / numPagos : 0;

        // Agrupar pagos por recibo para identificar abonos manuales múltiples
        const pagosPorRecibo = new Map<string, any[]>();
        (lot.payments || []).forEach((p: any) => {
          const recibo = p.receipt || p.receiptNumber || '-';
          if (!pagosPorRecibo.has(recibo)) {
            pagosPorRecibo.set(recibo, []);
          }
          pagosPorRecibo.get(recibo)!.push(p);
        });

        // Procesar pagos agrupados
        pagosPorRecibo.forEach((pagosGrupo, recibo) => {
          // Enganche
          const enganchePago = pagosGrupo.find((p: any) => p.type === 'enganche');
          if (enganchePago) {
            historialPagos.push({
              fecha: enganchePago.date,
              concepto: 'Enganche',
              tipoPago: 'Crédito',
              recibo: recibo,
              monto: enganchePago.amount,
              estado: 'Pagado',
              esGrupo: false,
            });
            return;
          }

          // Abonos
          const abonos = pagosGrupo.filter((p: any) => p.type === 'abono');
          if (abonos.length > 0) {
            // Si hay múltiples abonos con el mismo recibo, agruparlos
            if (abonos.length > 1) {
              const montoTotal = abonos.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
              const detalles: any[] = [];

              abonos.forEach((p: any) => {
                const cuota = p.cuotaNumero ? String(p.cuotaNumero) : '-';
                const monto = Number(p.amount || 0);
                const completo = cuotaMensual > 0 && Math.abs(monto - cuotaMensual) < 0.01;

                // Calcular el saldo restante de la cuota después de este pago
                const pagosCuota = (lot.payments || []).filter((p2: any) => {
                  return p2.type === 'abono' && String(p2.cuotaNumero) === cuota;
                });
                const totalPagadoCuota = pagosCuota.reduce((sum: number, p2: any) => sum + Number(p2.amount || 0), 0);
                const saldoCuota = Math.max(cuotaMensual - totalPagadoCuota, 0);

                if (completo) {
                  detalles.push({
                    cuota,
                    monto,
                    descripcion: 'completa cuota'
                  });
                } else if (saldoCuota < 0.01) {
                  detalles.push({
                    cuota,
                    monto,
                    descripcion: 'completa cuota'
                  });
                } else {
                  detalles.push({
                    cuota,
                    monto,
                    descripcion: `parcial, falta Q. ${saldoCuota.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                  });
                }
              });

              historialPagos.push({
                fecha: abonos[0].date,
                concepto: 'Abono Manual',
                tipoPago: 'Crédito',
                recibo: recibo,
                monto: montoTotal,
                estado: 'Pagado',
                esGrupo: true,
                detalles: detalles,
                observaciones: abonos[0].observaciones || ''
              });
            } else {
              // Abono individual
              const p = abonos[0];
              const cuota = p.cuotaNumero ? String(p.cuotaNumero) : '-';
              const monto = Number(p.amount || 0);
              const completo = cuotaMensual > 0 && Math.abs(monto - cuotaMensual) < 0.01;
              const parcial = cuotaMensual > 0 && monto < cuotaMensual;

              // Calcular el saldo restante de la cuota
              const pagosCuota = (lot.payments || []).filter((p2: any) => {
                return p2.type === 'abono' && String(p2.cuotaNumero) === cuota;
              });
              const totalPagadoCuota = pagosCuota.reduce((sum: number, p2: any) => sum + Number(p2.amount || 0), 0);
              const saldoCuota = Math.max(cuotaMensual - totalPagadoCuota, 0);

              const detalle = completo || saldoCuota < 0.01
                ? 'completo'
                : parcial
                  ? `parcial (falta Q. ${saldoCuota.toLocaleString(undefined, { minimumFractionDigits: 2 })})`
                  : '';

              historialPagos.push({
                fecha: p.date,
                concepto: `Abono cuota ${cuota}${detalle ? ' – ' + detalle : ''}`,
                tipoPago: 'Crédito',
                recibo: recibo,
                monto: p.amount,
                estado: 'Pagado',
                esGrupo: false,
              });
            }
          }
        });

        // Cuotas pendientes (considera abonos manuales y excedentes aplicados a cuotas futuras)
        const fechaVenta = new Date(lot.saleDate || new Date());
        for (let i = 1; i <= numPagos; i++) {
          const fechaVencimiento = new Date(fechaVenta);
          fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
          const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

          // Sumar pagos aplicados a esta cuota (incluye abonos manuales y parciales)
          const pagosCuota = (lot.payments || []).filter((p: any) => {
            if (p.type !== 'abono') return false;
            const sameCuota = String(p.cuotaNumero) === String(i);
            // Tolerar pequeñas diferencias de fecha de vencimiento formateada
            const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
            const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaVencimientoYMD;
            return sameCuota && sameVenc;
          });
          const montoPagadoCuota = pagosCuota.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);

          // Calcular monto restante para esta cuota
          const montoRestante = Math.max(cuotaMensual - montoPagadoCuota, 0);
          const pagada = montoRestante <= 0.0001; // tolerancia

          cuotasPendientes.push({
            cuota: i,
            fecha: fechaVencimientoYMD,
            montoCuota: cuotaMensual,
            pagado: montoPagadoCuota,
            saldo: pagada ? 0 : montoRestante,
            estado: pagada ? 'Pagada' : 'Próxima',
          });
        }
      }
    });
    historialPagos.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  }

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
        <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
          <div>
            <CardTitle className="text-xl font-semibold tracking-tight text-foreground">Estado de Cuenta</CardTitle>
            <CardDescription className="text-[13px] text-muted-foreground mt-1">Resumen financiero detallado por cliente</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 items-end mb-4">
            <div className="w-full max-w-sm">
              <Combobox
                value={selected?.value || ''}
                onValueChange={handleSelect}
                options={opcionesFiltradas}
                inputValue={search}
                onInputChange={setSearch}
                placeholder="Buscar por nombre, DPI o lote"
              />
            </div>
            {selected && (
              <Button variant="ghost" size="icon" onClick={handleLimpiar} title="Limpiar selección">
                <X className="h-5 w-5" />
              </Button>
            )}
            <div className="flex-1" />
            <Button
              className="bg-transparent border border-input hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
              disabled={!selected}
              onClick={() => setMostrar(true)}
            >
              Generar Estado de Cuenta
            </Button>
            <Button
              className="bg-secondary border border-input text-foreground hover:bg-accent rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3 flex items-center gap-1.5"
              onClick={exportarEstadosCuentaMasivo}
              title="Descargar estados de cuenta de todos los clientes"
            >
              <Download className="h-3.5 w-3.5" />
              Exportar Todos
            </Button>
          </div>

          {mostrar && lotesCliente.length > 0 && (
            <div className="space-y-6">
              {/* Historial de Pagos */}
              <Card className="mb-6 bg-card border-border shadow-none rounded-lg overflow-hidden">
                <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-medium tracking-tight">Historial de Pagos</CardTitle>
                    <span className="text-xs text-muted-foreground">{historialPagos.length} registros</span>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent border-b border-white/5">
                          <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Fecha</TableHead>
                          <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Concepto</TableHead>
                          <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Tipo de Pago</TableHead>
                          <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">No. Recibo</TableHead>
                          <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Monto</TableHead>
                          <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historialPagos.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground py-12">No hay pagos registrados</TableCell>
                          </TableRow>
                        ) : (
                          historialPagos.map((pago, idx) => (
                            <React.Fragment key={idx}>
                              <TableRow className={pago.esGrupo ? 'bg-blue-500/5 hover:bg-blue-500/10 cursor-pointer border-b border-white/5' : 'border-b border-white/5 hover:bg-white/[0.02] transition-colors'}>
                                <TableCell className={`px-3 py-3 text-[13px] ${pago.esGrupo ? 'font-semibold' : ''}`}>{pago.fecha}</TableCell>
                                <TableCell
                                  className={`px-3 py-3 text-[13px] ${pago.esGrupo ? 'font-semibold' : ''}`}
                                  onClick={() => pago.esGrupo && togglePaymentDetails(idx)}
                                >
                                  <div className="flex items-center gap-2">
                                    {pago.esGrupo && (
                                      expandedPayments.has(idx) ?
                                        <ChevronDown className="h-4 w-4 text-blue-500" /> :
                                        <ChevronRight className="h-4 w-4 text-blue-500" />
                                    )}
                                    {pago.esGrupo && '📥 '}
                                    {pago.concepto}
                                  </div>
                                </TableCell>
                                <TableCell className="px-3 py-3 text-[13px]">{pago.tipoPago}</TableCell>
                                <TableCell className="px-3 py-3 font-mono text-[13px]">{pago.recibo}</TableCell>
                                <TableCell className={`px-3 py-3 font-mono text-[13px] text-emerald-500 ${pago.esGrupo ? 'font-semibold' : ''}`}>
                                  Q. {parseFloat(pago.monto).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="px-3 py-3 text-[13px]">{pago.estado}</TableCell>
                              </TableRow>
                              {pago.esGrupo && pago.detalles && pago.detalles.length > 0 && expandedPayments.has(idx) && (
                                <TableRow className="bg-blue-500/5 border-l-4 border-blue-500">
                                  <TableCell colSpan={6} className="py-2">
                                    <div className="ml-8 space-y-1 text-sm">
                                      <div className="font-medium text-foreground mb-1">Distribución del pago:</div>
                                      {pago.detalles.map((detalle: any, dIdx: number) => (
                                        <div key={dIdx} className="flex items-center gap-2 text-muted-foreground">
                                          <span className="text-blue-500">├─</span>
                                          <span>Cuota {detalle.cuota}:</span>
                                          <span className="font-medium text-foreground">Q. {parseFloat(detalle.monto).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                          <span className="text-muted-foreground/70">({detalle.descripcion})</span>
                                        </div>
                                      ))}
                                      {pago.observaciones && (
                                        <div className="flex items-center gap-2 text-muted-foreground/70 italic mt-2">
                                          <span className="text-blue-500">└─</span>
                                          <span>Observación: {pago.observaciones}</span>
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Cuotas Pendientes solo si es crédito */}
              {esCredito && (
                <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
                  <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-sm font-medium tracking-tight">Cuotas Pendientes</CardTitle>
                      <span className="text-xs text-muted-foreground">{cuotasPendientes.length} cuotas</span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent border-b border-white/5">
                            <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Cuota No.</TableHead>
                            <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Fecha Vencimiento</TableHead>
                            <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Monto cuota</TableHead>
                            <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Pagado</TableHead>
                            <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Saldo</TableHead>
                            <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Estado</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {cuotasPendientes.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center text-muted-foreground py-12">No hay cuotas pendientes</TableCell>
                            </TableRow>
                          ) : (
                            cuotasPendientes.map((c, idx) => (
                              <TableRow key={idx} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                                <TableCell className="px-3 py-3 text-[13px]">{c.cuota}</TableCell>
                                <TableCell className="px-3 py-3 text-[13px]">{c.fecha}</TableCell>
                                <TableCell className="px-3 py-3 font-mono text-[13px]">Q. {parseFloat(c.montoCuota).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className="px-3 py-3 font-mono text-[13px] text-emerald-500">Q. {parseFloat(c.pagado || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className="px-3 py-3 font-mono text-[13px] text-amber-500">Q. {parseFloat(c.saldo || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className="px-3 py-3 text-[13px]">{c.estado === 'Pagada' ? 'Pagada' : 'Próxima'}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 